import React, { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { LoginForm } from '../components/Login/LoginForm'
import { BarChart3, Factory, ShieldCheck } from 'lucide-react'

export const Login: React.FC = () => {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user && !isLoading) {
      const routes: Record<string, string> = {
        admin: '/dashboard/admin', sales: '/dashboard/sales',
        teamleader: '/dashboard/teamleader', forklift: '/dashboard/forklift',
        technical: '/dashboard/technical',
      }
      const route = routes[user.role]
      if (route) navigate(route, { replace: true })
    }
  }, [user, isLoading, navigate])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-7 w-7 text-kvh-red" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
          </svg>
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Laden…</span>
        </div>
      </div>
    )
  }

  if (user) {
    const routes: Record<string, string> = {
      admin: '/dashboard/admin', sales: '/dashboard/sales',
      teamleader: '/dashboard/teamleader', forklift: '/dashboard/forklift',
      technical: '/dashboard/technical',
    }
    return <Navigate to={routes[user.role]} replace />
  }

  return (
    <div className="login-shell min-h-screen">
      <section className="login-brand-panel">
        <div className="relative z-10 max-w-xl">
          <div className="brand-lockup"><img src="/assets/logo.png" alt="KVH" /></div>
          <p className="mt-14 text-xs font-semibold uppercase tracking-[0.22em] text-white/55">Operations platform</p>
          <h1 className="mt-4 text-4xl xl:text-5xl font-semibold tracking-[-0.04em] leading-[1.08] text-white text-balance">
            Grip op productie.<br />Rust in de operatie.
          </h1>
          <p className="mt-5 text-base leading-7 text-white/65 max-w-md">
            Eén veilige werkplek voor machines, kwaliteit, taken en rapportages.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-10">
            {[
              { icon: Factory, label: 'Machinepark' },
              { icon: BarChart3, label: 'Kwaliteit' },
              { icon: ShieldCheck, label: 'Veilige toegang' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="login-feature"><Icon size={18} /><span>{label}</span></div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-xs text-white/35">KVH Kunststofbewerking · intern gebruik</p>
      </section>

      <section className="login-form-panel">
        <div className="w-full max-w-[390px]">
          <div className="lg:hidden flex justify-center mb-8"><img src="/assets/logo.png" alt="KVH" className="w-28" /></div>
          <LoginForm onLoginSuccess={() => {}} />
          <div className="flex items-center justify-center gap-2 mt-6 text-xs" style={{ color: 'var(--text-tertiary)' }}>
            <ShieldCheck size={13} /> Beveiligde KVH-omgeving
          </div>
        </div>
        <a
          href="http://watalsweplasticnietmeeralsafvalbeschouwen.info"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-5 text-[11px] transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
        >
          Wat als we plastic niet meer als afval beschouwen?
        </a>
      </section>
    </div>
  )
}
